<template>
  <FlexLayout col>
    <SectionTitle hideBtn>
      <SVGIcon :icon="isOrgPoint ? 'common-org_icon' : 'common-shop_icon'" stroke="var(--gray-80)" fill="transparent" width="20" height="20" />
      {{ tableTitle }}
      <span v-if="showTaipeiMetroHint" class="text-normal text-gray-60">
        {{ hintText }}
      </span>
    </SectionTitle>
    <FiltersContainer v-if="showOperationBranchFilter" class="my-5" flat>
      <ShopFilterSelect
        :value.sync="recordFilters.operationShopId"
        :placeholder="$t('member.tab.point.operationBranch')"
        @change="handleFilterChange"
        @clear="handleFilterChange"
      />
    </FiltersContainer>
    <BaseTable v-loading="loading" :data="displayTableData">
      <BaseElTableColumn prop="createdAt" :label="columnText.createdAt" align="center" />
      <BaseElTableColumn prop="type" :label="columnText.type" align="center" />
      <BaseElTableColumn prop="amount" :label="columnText.amount" align="center">
        <template slot-scope="scope">
          <p :class="{'text-danger': scope.row.amount < 0}">
            <span>{{ scope.row.amount > 0 ? '+' : '' }}</span>
            <span>{{ scope.row.amount }}</span>
          </p>
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showBranchColumn" prop="Branch" :label="columnText.Branch" align="center">
        <template slot-scope="scope">
          {{ scope.row.Branch?.name || "-" }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn prop="originMessage" :label="columnText.originMessage" align="center">
        <template slot-scope="scope">{{ scope.row.originMessage || '-' }}</template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showOperationBranchFilter" prop="IssuingBranch" :label="$t('member.tab.point.issuingBranch')" align="center">
        <template slot-scope="scope">{{ scope.row.IssuingBranch?.name || '-' }}</template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showOperationBranchFilter" prop="OperationBranch" :label="$t('member.tab.point.operationBranchColumn')" align="center">
        <template slot-scope="scope">{{ scope.row.GrantedShop?.name || scope.row.RedemptionShop?.name || "-" }}</template>
      </BaseElTableColumn>
    </BaseTable>
    <Pagination
      class="pagination"
      :curPage.sync="tableOptions.page"
      :pageLimit="tableOptions.pageLimit"
      :total="tableOptions.dataCount"
      @pageChange="pageChange"
    />
  </FlexLayout>
</template>

<script>
import {
  FindPointWalletRecord,
  FindPointWalletRecordCount,
  FindExternalPointWalletRecord,
  CountExternalPointWalletRecord,
} from '@/api/shopPoint'
import { computed, defineComponent, inject, onMounted, ref, reactive } from 'vue'
import { map, get, find } from 'lodash'
import store from '@/store'
import { useTable } from '@/use/table'
import { useShop } from '@/use/shop'
import { useShopPoint } from '@/use/useShopPoint'
import { useOrgBenefitControl } from '@/use/useOrgBenefitControl'
import { originConfig } from '@/config/point'
import ShopFilterSelect from '@/components/Select/ShopFilterSelect.vue'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'PointRecordList',
  components: { ShopFilterSelect },
  props: {
    memberId: {
      type: String,
    },
    shopPointKey: {
      type: String,
      required: true,
    },
    isExternalPoint: {
      type: Boolean,
      default: false,
    },
    showBranchColumn: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const { shopPointList } = useShop()
    const { isTaipeiMetroPoint } = useShopPoint()
    const pageShopPoint = inject('pageShopPoint')
    const { pageStartIndex, dateFormat, tableOptions, tableData } = useTable()
    const { enableOrgBenefitSharing, isPointBenefitSharingEnabled } = useOrgBenefitControl()
    const shopId = computed(() => store.getters.shop)
    const loading = ref(false)
    const recordFilters = reactive({
      operationShopId: null,
    })

    const showOperationBranchFilter = computed(() => isPointBenefitSharingEnabled.value)

    const isOrgPoint = computed(() => enableOrgBenefitSharing.value && pageShopPoint.value?.sourceShopIsHeadquarter)

    const tableTitle = computed(() => {
      return props.isExternalPoint
        ? `${get(pageShopPoint.value, 'name', t('member.tab.point.defaultName'))}${t('member.tab.point.historyRecord')}`
        : t('member.tab.point.usageRecord')
    })

    const columnText = computed(() => ({
      createdAt: t('member.tab.point.createdAt'),
      type: t('member.tab.point.type'),
      amount: get(pageShopPoint.value, 'name', t('member.tab.point.defaultName')),
      Branch: t('member.tab.point.operationUnit'),
      originMessage: t('member.tab.point.remark'),
    }))

    const showTaipeiMetroHint = computed(() => {
      return props.isExternalPoint && isTaipeiMetroPoint(find(shopPointList.value, { key: props.shopPointKey }))
    })

    const hintText = t('member.tab.point.taipeiMetroHint')

    const displayTableData = computed(() => {
      return map(tableData.value, (item) => ({
        ...item,
        createdAt: dateFormat(item.createdAt),
        type: get(originConfig, `${item.origin}.name`),
        IssuingBranch: item.IssuingBranch ?? item.Shop ?? null,
        OperationBranch: item.DistributingBranch ?? item.GrantedShop ?? item.RedemptionBranch ?? item.RedemptionShop ?? null,
      }))
    })

    const fetchData = async () => {
      const apiFunc = props.isExternalPoint ? FindExternalPointWalletRecord : FindPointWalletRecord
      const [res, err] = await apiFunc({
        shopId: shopId.value,
        memberId: props.memberId,
        start: pageStartIndex.value,
        limit: tableOptions.pageLimit,
        shopPointKey: props.shopPointKey,
        operationShopId: recordFilters.operationShopId || undefined,
      })
      if (err) return window.$message.error(err)
      tableData.value = res
    }

    const fetchCount = async () => {
      const apiFunc = props.isExternalPoint ? CountExternalPointWalletRecord : FindPointWalletRecordCount
      const [res, err] = await apiFunc({
        shopId: shopId.value,
        memberId: props.memberId,
        shopPointKey: props.shopPointKey,
        operationShopId: recordFilters.operationShopId || undefined,
      })
      if (err) return window.$message.error(err)
      tableOptions.dataCount = res.count
    }

    const handleFilterChange = async () => {
      tableOptions.page = 1
      await refresh()
    }

    const pageChange = async () => {
      loading.value = true
      await Promise.all([fetchData(), fetchCount()])
      loading.value = false
    }

    const refresh = async () => {
      loading.value = true
      await Promise.all([fetchData(), fetchCount()])
      loading.value = false
    }

    onMounted(refresh)

    return {
      loading,
      tableOptions,
      displayTableData,
      tableTitle,
      columnText,
      showTaipeiMetroHint,
      hintText,
      isOrgPoint,
      showOperationBranchFilter,
      recordFilters,
      pageChange,
      refresh,
      handleFilterChange,
    }
  },
})
</script>
